// Aplicacao
import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import { version } from '../package'

// Dependencias
import * as Sentry from '@sentry/vue'
import VueResource from 'vue-resource'
import VueSocketIO from 'vue-socket.io'
import SocketIO from 'socket.io-client'
import VueSwal from 'vue-swal'
import VueTheMask from 'vue-the-mask'
import money from 'v-money'
import dayjs from 'dayjs'

// Plugins locais
import './plugins/dayjs'
import AudioPlugin from './plugins/audio'
import GoogleMapsPlugin from './plugins/google-maps'
import CachePlugin from './plugins/cache-plugin'
import helpers from './helpers'

// Framework CSS
import './assets/scss/bootstrap-custom.scss'

// Estilos de dependencias
import 'animate.css/animate.css'
import '@fortawesome/fontawesome-free/css/fontawesome.css'
import '@fortawesome/fontawesome-free/css/solid.css'
import 'leaflet/dist/leaflet.css'

// Estilos da aplicacao
import './assets/css/modal-pdv.css'
import './assets/css/utilities.css'
import './assets/css/style.css'

// Componentes globais
import Cliente from './views/comanda/pdv/Cliente'
import Finalizar from './views/comanda/pdv/Finalizar'
import AdminVendas from './views/comanda/comanda/AdminVendas'
import Pedido from './views/comanda/pdv/Pedido'
import ListaPedidos from './views/comanda/pdv/ListaPedidos'

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    Vue,
    dsn: "https://66ded35d5efa4bfc29a56eb717843ce1@o4504123718565888.ingest.us.sentry.io/4508886823665664",
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.5,
    tracePropagationTargets: [],
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

if (typeof localStorage !== "undefined") {
  Vue.use(VueResource);
  Vue.use(VueSwal);
  Vue.use(helpers);
  Vue.use(VueTheMask);
  Vue.use(AudioPlugin);
  Vue.use(GoogleMapsPlugin);

  Vue.use(money, {
    decimal: ',',
    thousands: '.',
    prefix: 'R$ ',
    suffix: '',
    precision: 2,
    masked: false
  });

  Vue.use(CachePlugin, {
    prefix: "pdv_",
    autoload: true,
    vuex: store,
    mapVuex: {
      configs: "setConfigs",
      zonas: "setZonas",
      pagamentos: "setPagamentos",
      produtos: "setProdutos",
      termos: "setTermos"
    }
  });

  const urlParams = new URLSearchParams(window.location.search);
  let token = urlParams.get('auth');

  if (token) {
    localStorage.setItem('key', token);
  }

  token = token || localStorage.getItem('key') || null;
  const socket_auth = process.env.VUE_APP_SOCKET_AUTH || null;

  if (token && socket_auth) {
    const base_socket = process.env.VUE_APP_BASE_SOCKET;
    const socketConnection = SocketIO(base_socket, {
      transports: ["websocket", "polling"],
      withCredentials: true,
      auth: { token: socket_auth },
    });

    Vue.use(new VueSocketIO({
      debug: false,
      connection: socketConnection,
    }));
  }

  const isElectron = (!!window.Electron || !!sessionStorage.getItem('ElectronV'));
  const electronV = parseInt((window.ElectronV || sessionStorage.getItem('ElectronV') || '181').replace(/[^0-9]/g, ''));
  const is_homolog = process.env.VUE_APP_HOMOLOG === 'true';
  const base_fallback = getBaseFallback();
  const base_server = getBaseServer(is_homolog);

  if (isElectron) {
    sessionStorage.setItem('ElectronV', (electronV || '').toString());
  }

  Vue.mixin({
    data () {
      return {
        electronV,
        isElectron,
        base_server,
        is_homolog,
        is_fallback: !is_homolog && !!base_fallback && base_server === base_fallback,
        server_alternativo: base_fallback,
        server_teste: 'https://dev.lecard.app.br/',
        app_version: version,
        is_mobile: window.matchMedia("(pointer: coarse)").matches,
        is_beta: process.env.VUE_APP_BETA === 'true',
        base_cdn: (process.env.VUE_APP_BASE_CDN || "https://lecard-cdn.nyc3.cdn.digitaloceanspaces.com")
      }
    },

    methods: {
      async resolveServer(reload = true, base_server = this.base_server) {
        const result = await this.$server.getAvailableBaseServer(base_server, base_fallback, this.is_homolog);

        if (reload && result.server_available && result.base_server !== base_server) {
          window.location.reload();
          return { ...result, reloading: true };
        }

        return result;
      },

      playNotification(som) {
        if (!this.$store.state.canPlay) return;

        if (som === 2) {
          this.$audio.play("notificacao");

        } else {
          document.dispatchEvent(new CustomEvent('playNotification'));
          this.$audio.play("pedidos");
        }
      },

      pauseNotification() {
        document.dispatchEvent(new CustomEvent('pauseNotification'));
        this.$audio.pause("pedidos");
      },

      logout() {
        localStorage.removeItem('key');
        localStorage.removeItem('token');
        this.$cache.resetAll();
        sessionStorage.clear();

        if (this.$route.query.id) {
          sessionStorage.setItem('redirect', this.$route.fullPath);
        }

        window.location.reload();
      },

      formatarData(data, formato = 'DD/MM/YYYY - HH:mm', offsetHoras = '0') {
        return data ? dayjs(data).add(Number(offsetHoras), 'hour').format(formato) : ''
      },
    }
  });

  Vue.filter('valor', function (value) {
    if (value === undefined || value === null) {
      return value;
    }
    let formatter = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });
    return formatter.format(value);
  });

  Vue.filter('percent', function (value) {
    if (value !== null || value !== undefined) {
      return parseFloat(value).toFixed();
    }
  });

  Vue.filter('kilometer', function (value) {
    if (value !== null || value !== undefined) {
      const formated = parseFloat(value / 1000).toFixed(1);
      return formated + ' km';
    }
  });

  Vue.filter('phone', function (phone) {
    if (phone) {
      phone = phone.length > 11 ? phone.substring(2, 13) : phone;

      if (phone.length === 10) {
        return phone.replace(/[^0-9]/g, '')
          .replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');

      } else if (phone.length === 11) {
        return phone.replace(/[^0-9]/g, '')
          .replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
    }

    return phone
  });

  Vue.filter('tempo', function (value) {
    if (!isNaN(value)) {
      value = parseInt(value || '0');

      if (!value) {
        return `Hoje`;

      } else {
        return value >= 1440 ? `${(value / 1440).toFixed()} dia${value > 2159 ? 's' : ''}` : `${value} mins`;
      }
    }
  });

  Vue.filter('fDate', function (value, formato = 'DD/MM/YYYY - HH:mm') {
    if (!value) return '';
    return dayjs(value).format(formato);
  });

  Vue.config.productionTip = false;
  Vue.http.options.emulateJSON = true;
  Vue.http.options.emulateHTTP = true;
  Vue.http.options.root = base_server + 'api/';
  let x_auth = null;

  Vue.http.interceptors.push((request, next) => {
    x_auth = x_auth || localStorage.getItem('x_auth') || null;

    if (!request.skipHeader) {
      request.headers.set('x-lecard-key', x_auth);
    }

    next();
  });

  Vue.component("Cliente", Cliente);
  Vue.component("Finalizar", Finalizar);
  Vue.component("AdminVendas", AdminVendas);
  Vue.component("Pedido", Pedido);
  Vue.component("ListaPedidos", ListaPedidos);

  Date.prototype.toDateInputValue = (function() {
    let local = new Date(this);
    local.setMinutes(this.getMinutes() - this.getTimezoneOffset());
    return local.toJSON().slice(0,10);
  });

  Date.prototype.toTimeInputValue = (function() {
    let local = new Date(this);
    local.setMinutes(this.getMinutes() - this.getTimezoneOffset());
    return local.toJSON().slice(11,16);
  });

  new Vue({
    router,
    store,
    render: h => h(App)
  }).$mount('#app');

} else {
  document.body.innerHTML = "<div style='text-align: center; padding-top: 44vh'><h6>Ops, erro ao carregar a página.</h6></div>";
}

function getBaseServer(is_homolog) {
  const session_server = !is_homolog ? sessionStorage.getItem('base_server') : null;
  return normalizeBaseUrl(session_server || process.env.VUE_APP_BASE_SERVER);
}

function getBaseFallback() {
  const base_fallback = process.env.VUE_APP_BASE_FALLBACK;
  return base_fallback ? normalizeBaseUrl(base_fallback) : null;
}

function normalizeBaseUrl(base_server) {
  return base_server.endsWith('/') ? base_server : base_server + '/';
}
